/**
 * ExcelCodeFilterSelect
 * ตัวเลือกรหัส (Code Picker) แบบ Excel-like Filter สำหรับลิสต์ขนาดใหญ่ (1,000+ รายการ)
 * รองรับ: ค้นหา real-time (เฉพาะ code, debounce 200ms), sort A→Z/Z→A (natural),
 * โหมดแสดงทั้งหมด/เฉพาะที่เลือก, เลือกทั้งหมด-ล้าง เฉพาะรายการที่กรอง, ตัวนับที่เลือก,
 * ปักหมุดรายการที่เลือกไว้บนสุด, chip tag ใน input, virtual scroll, คีย์บอร์ด, Esc คืนค่าเดิม
 *
 * ใช้สัญญา props แบบเดียวกับ GoogleExcelSelectSearch (data / selectedRows / onSelectionChange)
 * เพื่อให้สลับใช้งานในหน้าเดิมได้ทันที โดย value ของแต่ละแถวคือ "code"
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import {
    Box,
    Checkbox,
    Chip,
    ClickAwayListener,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    Popper,
    TextField,
    Tooltip,
    Typography
} from '@mui/material'

import {
    faCaretDown,
    faCheck,
    faSearch,
    faSortAlphaDown,
    faSortAlphaUp,
    faTimes
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { colors } from 'constants/theme'
// react-window ถูกประกาศเป็น untyped module (ดู src/react-window.d.ts)
import { FixedSizeList } from 'react-window'

export interface CodeRow {
    id: string | number
    value: string
    label?: string
    [key: string]: any
}

interface ExcelCodeFilterSelectProps {
    data: CodeRow[]
    selectedRows: CodeRow[]
    onSelectionChange: (rows: CodeRow[]) => void
    inputLabel?: string
    placeholder?: string
    disabled?: boolean
    /** จำนวน chip สูงสุดที่แสดงใน input ก่อนยุบเป็น "+N" */
    maxTags?: number
    /** ความสูงแถวในลิสต์ (px) */
    rowHeight?: number
    /** ความสูงสูงสุดของลิสต์ก่อน scroll (px) */
    maxListHeight?: number
}

type ListItem = { kind: 'divider' } | { kind: 'option'; row: CodeRow }

// @types/react@17 + MUI v5 ทำให้ Popper เรียกร้อง props nonce/onResize เกินจำเป็น — cast เพื่อข้าม
const SafePopper = Popper as unknown as (props: any) => JSX.Element

/** เรียงแบบ natural: "01-2" มาก่อน "01-11" */
const naturalCompare = (a: string, b: string) =>
    String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })

export default function ExcelCodeFilterSelect(props: ExcelCodeFilterSelectProps) {
    const {
        data = [],
        selectedRows = [],
        onSelectionChange,
        inputLabel = 'Code',
        placeholder = 'ค้นหา Code...',
        disabled = false,
        maxTags = 3,
        rowHeight = 38,
        maxListHeight = 320
    } = props

    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
    const [selectedOnly, setSelectedOnly] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const snapshotRef = useRef<CodeRow[]>([])

    const anchorRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<any>(null)
    const searchRef = useRef<HTMLInputElement>(null)

    /** debounce 200ms */
    useEffect(() => {
        const id = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 200)
        return () => clearTimeout(id)
    }, [query])

    const selectedCodes = useMemo(() => new Set(selectedRows.map((r) => r.value)), [selectedRows])

    /** กรองเฉพาะ code (case-insensitive contains) */
    const filtered = useMemo(() => {
        if (!debouncedQuery) return data
        return data.filter((r) => String(r.value).toLowerCase().includes(debouncedQuery))
    }, [data, debouncedQuery])

    /** sort + ปักหมุดรายการที่เลือกไว้บนสุด + แทรกเส้นแบ่ง */
    const listItems = useMemo<ListItem[]>(() => {
        const sorted = [...filtered].sort((a, b) =>
            sortDir === 'asc' ? naturalCompare(a.value, b.value) : naturalCompare(b.value, a.value)
        )
        const picked = sorted.filter((r) => selectedCodes.has(r.value))
        if (selectedOnly) return picked.map((row) => ({ kind: 'option', row }))

        const rest = sorted.filter((r) => !selectedCodes.has(r.value))
        const items: ListItem[] = picked.map((row) => ({ kind: 'option', row }))
        if (picked.length > 0 && rest.length > 0) items.push({ kind: 'divider' })
        rest.forEach((row) => items.push({ kind: 'option', row }))
        return items
    }, [filtered, sortDir, selectedOnly, selectedCodes])

    /** index ของรายการที่เลือกได้ (ข้ามเส้นแบ่ง) ใช้กับคีย์บอร์ด */
    const selectableIndexes = useMemo(
        () => listItems.reduce<number[]>((acc, it, i) => (it.kind === 'option' ? [...acc, i] : acc), []),
        [listItems]
    )

    const totalCount = data.length
    const selectedCount = selectedRows.length

    // reset ตำแหน่ง active เมื่อผลลัพธ์เปลี่ยน
    useEffect(() => {
        setActiveIndex(selectableIndexes[0] ?? -1)
    }, [selectableIndexes])

    const emit = useCallback(
        (rows: CodeRow[]) => onSelectionChange(rows),
        [onSelectionChange]
    )

    const toggleRow = useCallback(
        (row: CodeRow) => {
            if (selectedCodes.has(row.value)) emit(selectedRows.filter((r) => r.value !== row.value))
            else emit([...selectedRows, row])
        },
        [selectedCodes, selectedRows, emit]
    )

    /** เลือกทั้งหมด* / ล้าง* ทำงานเฉพาะรายการที่กรองอยู่ (เหมือน Excel) */
    const selectAllFiltered = useCallback(() => {
        const map = new Map(selectedRows.map((r) => [r.value, r]))
        filtered.forEach((r) => map.set(r.value, r))
        emit(Array.from(map.values()))
    }, [filtered, selectedRows, emit])

    const clearFiltered = useCallback(() => {
        const filteredCodes = new Set(filtered.map((r) => r.value))
        emit(selectedRows.filter((r) => !filteredCodes.has(r.value)))
    }, [filtered, selectedRows, emit])

    const handleOpen = useCallback(() => {
        if (disabled) return
        snapshotRef.current = selectedRows
        setOpen(true)
        setTimeout(() => searchRef.current?.focus(), 0)
    }, [disabled, selectedRows])

    const applyAndClose = useCallback(() => {
        setOpen(false)
        setQuery('')
    }, [])

    /** Esc/ยกเลิก: คืนค่า selection กลับเป็นตอนเปิด */
    const cancelAndClose = useCallback(() => {
        emit(snapshotRef.current)
        setOpen(false)
        setQuery('')
    }, [emit])

    const moveActive = useCallback(
        (dir: 1 | -1) => {
            if (!selectableIndexes.length) return
            const pos = selectableIndexes.indexOf(activeIndex)
            const nextPos =
                pos === -1 ? 0 : Math.min(Math.max(pos + dir, 0), selectableIndexes.length - 1)
            const next = selectableIndexes[nextPos]
            setActiveIndex(next)
            listRef.current?.scrollToItem?.(next)
        },
        [selectableIndexes, activeIndex]
    )

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault()
                    moveActive(1)
                    break
                case 'ArrowUp':
                    e.preventDefault()
                    moveActive(-1)
                    break
                case ' ':
                    e.preventDefault()
                    {
                        const it = listItems[activeIndex]
                        if (it && it.kind === 'option') toggleRow(it.row)
                    }
                    break
                case 'Enter':
                    e.preventDefault()
                    applyAndClose()
                    break
                case 'Escape':
                    e.preventDefault()
                    cancelAndClose()
                    break
                default:
                    break
            }
        },
        [moveActive, listItems, activeIndex, toggleRow, applyAndClose, cancelAndClose]
    )

    const visibleTags = selectedRows.slice(0, maxTags)
    const extraTags = selectedCount - visibleTags.length

    const listHeight = Math.min(listItems.length * rowHeight, maxListHeight) || rowHeight
    const [popperWidth, setPopperWidth] = useState<number>(320)
    useLayoutEffect(() => {
        if (open && anchorRef.current) setPopperWidth(anchorRef.current.offsetWidth)
    }, [open])

    const Row = useCallback(
        ({ index, style }: { index: number; style: any }) => {
            const item = listItems[index]
            if (!item) return null
            if (item.kind === 'divider') {
                return (
                    <Box style={style} sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                        <Divider sx={{ width: '100%', borderColor: colors.lightGray }} />
                    </Box>
                )
            }
            const { row } = item
            const checked = selectedCodes.has(row.value)
            const isActive = index === activeIndex
            return (
                <Box
                    style={style}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => toggleRow(row)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 0.5,
                        cursor: 'pointer',
                        backgroundColor: isActive ? colors.themeSecondColor06 || '#f0f6ff' : 'transparent',
                        '&:hover': { backgroundColor: colors.themeSecondColor06 || '#f0f6ff' }
                    }}
                >
                    <Checkbox
                        size="small"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                        sx={{
                            p: 0.5,
                            mr: 0.5,
                            color: colors.black12,
                            '&.Mui-checked': { color: colors.themeSecondColor },
                        }}
                    />
                    <Box sx={{ overflow: 'hidden' }}>
                        <Typography noWrap sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.2 }}>
                            {row.value}
                        </Typography>
                    </Box>
                </Box>
            )
        },
        [listItems, selectedCodes, activeIndex, toggleRow]
    )

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            {/* กล่อง input หลัก แสดง chip ที่เลือก */}
            <Box
                ref={anchorRef}
                onClick={handleOpen}
                sx={{
                    position: 'relative',
                    border: `1px solid ${open ? colors.themeSecondColor : colors.black12}`,
                    borderRadius: '8px',
                    minHeight: 40,
                    backgroundColor: disabled ? '#F0F0F0' : colors.white,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    px: 1.25,
                    py: 0.5,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'border-color 120ms ease',
                    '&:hover': {
                        borderColor: disabled ? colors.black12 : colors.themeSecondColor,
                    },
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        position: 'absolute',
                        top: -9,
                        left: 8,
                        px: 0.5,
                        fontSize: 12,
                        backgroundColor: colors.white,
                        color: open ? colors.themeSecondColor : colors.gray
                    }}
                >
                    {inputLabel}
                </Typography>

                {selectedCount === 0 && (
                    <Typography sx={{ fontSize: 14, color: colors.gray }}>{placeholder}</Typography>
                )}

                {visibleTags.map((r) => (
                    <Chip
                        key={r.value}
                        size="small"
                        label={r.value}
                        onDelete={
                            disabled
                                ? undefined
                                : (e) => {
                                      e.stopPropagation()
                                      emit(selectedRows.filter((x) => x.value !== r.value))
                                  }
                        }
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            maxWidth: 180,
                            height: 22,
                            fontSize: '12px',
                            backgroundColor: `${colors.themeSecondColor}1A`,
                            color: colors.themeSecondColor,
                            '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                            '& .MuiChip-deleteIcon': {
                                color: colors.themeSecondColor,
                                '&:hover': { color: colors.themeSecondDarkColor },
                            },
                        }}
                    />
                ))}
                {extraTags > 0 && (
                    <Chip
                        size="small"
                        label={`+${extraTags}`}
                        onClick={handleOpen}
                        sx={{
                            height: 22,
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: `${colors.themeSecondColor}1A`,
                            color: colors.themeSecondColor,
                            flexShrink: 0,
                        }}
                    />
                )}

                <Box sx={{ flex: 1 }} />
                <FontAwesomeIcon icon={faCaretDown} style={{ fontSize: 14, color: colors.black }} />
            </Box>

            <SafePopper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                style={{ zIndex: 1400, width: popperWidth }}
            >
                <ClickAwayListener onClickAway={applyAndClose}>
                    <Paper
                        elevation={3}
                        sx={{
                            mt: 0.5,
                            overflow: 'hidden',
                            borderRadius: '8px',
                            border: `1px solid ${colors.black12}`,
                        }}
                    >
                        {/* ส่วนหัว (ค้นหา/เครื่องมือ/ตัวนับ) เป็นชั้นทึบ zIndex สูงกว่า list กันแถวที่ scroll เลื่อนมาทับหัว */}
                        <Box sx={{ position: 'relative', zIndex: 2, backgroundColor: colors.white }}>
                        {/* แถวค้นหา */}
                        <Box sx={{ p: 1, pb: 0.5 }}>
                            <TextField
                                inputRef={searchRef}
                                fullWidth
                                size="small"
                                value={query}
                                placeholder={placeholder}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FontAwesomeIcon icon={faSearch} style={{ fontSize: 13, color: colors.gray }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: query ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setQuery('')}>
                                                <FontAwesomeIcon icon={faTimes} style={{ fontSize: 13 }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        '& fieldset': { borderColor: colors.black12 },
                                        '&:hover fieldset': { borderColor: colors.themeSecondColor },
                                        '&.Mui-focused fieldset': { borderColor: colors.themeSecondColor, borderWidth: '1px' },
                                    },
                                }}
                            />
                        </Box>

                        {/* แถบเครื่องมือ */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, pb: 0.5, flexWrap: 'wrap' }}>
                            <Tooltip title={sortDir === 'asc' ? 'เรียง A→Z (กดเพื่อสลับ)' : 'เรียง Z→A (กดเพื่อสลับ)'}>
                                <IconButton size="small" onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
                                    <FontAwesomeIcon icon={sortDir === 'asc' ? faSortAlphaDown : faSortAlphaUp} style={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={selectedOnly ? 'แสดงทั้งหมด' : 'แสดงเฉพาะที่เลือก'}>
                                <Chip
                                    size="small"
                                    icon={selectedOnly ? <FontAwesomeIcon icon={faCheck} style={{ fontSize: 11, color: '#FFFFFF' }} /> : undefined}
                                    label={selectedOnly ? 'เฉพาะที่เลือก' : 'ทั้งหมด'}
                                    variant={selectedOnly ? 'filled' : 'outlined'}
                                    onClick={() => setSelectedOnly((v) => !v)}
                                    sx={selectedOnly
                                        ? {
                                            backgroundColor: colors.themeSecondColor,
                                            color: '#FFFFFF',
                                            '&:hover': { backgroundColor: colors.themeSecondColor },
                                        }
                                        : {
                                            borderColor: colors.black12,
                                            color: colors.gray,
                                        }
                                    }
                                />
                            </Tooltip>

                            <Box sx={{ flex: 1 }} />

                            <Tooltip title="เลือกทุกรายการที่กรองอยู่">
                                <Typography
                                    onClick={selectAllFiltered}
                                    sx={{ fontSize: 13, color: colors.themeSecondColor, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    เลือกทั้งหมด*
                                </Typography>
                            </Tooltip>
                            <Typography sx={{ color: colors.lightGray }}>|</Typography>
                            <Tooltip title="ล้างเฉพาะรายการที่กรองอยู่">
                                <Typography
                                    onClick={clearFiltered}
                                    sx={{ fontSize: 13, color: colors.statusInactive || '#b00', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    ล้าง*
                                </Typography>
                            </Tooltip>
                        </Box>

                        {/* ตัวนับ */}
                        <Box sx={{ px: 1, pb: 0.5 }}>
                            <Typography sx={{ fontSize: 12, color: colors.gray }}>
                                เลือก {selectedCount} จาก {totalCount} รายการ
                            </Typography>
                        </Box>

                        <Divider />
                        </Box>

                        {/* ลิสต์ virtual — อยู่ชั้นล่างกว่าหัว (zIndex ต่ำกว่า) และ clip ด้วย overflow */}
                        <Box sx={{ position: 'relative', zIndex: 1, overflow: 'hidden' }}>
                        {listItems.length === 0 ? (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: 13, color: colors.gray }}>
                                    {`ไม่พบ Code ที่ตรงกับ "${query}"`}
                                </Typography>
                            </Box>
                        ) : (
                            <FixedSizeList
                                ref={listRef}
                                height={listHeight}
                                itemCount={listItems.length}
                                itemSize={rowHeight}
                                width={'100%'}
                            >
                                {Row}
                            </FixedSizeList>
                        )}
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </SafePopper>
        </Box>
    )
}
